import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { compose } from "redux";
import qs from "query-string";
import { orderBy } from "lodash";
import { gql, useQuery } from "@apollo/client";

export const PROPERTY_TYPES = [
  "ALL",
  "BUNGALOW",
  "DETACHED",
  "END_OF_TERRACE",
  "FLAT",
  "MAISONETTE",
  "SEMI_DETACHED",
  "TERRACED",
];

export const SEARCH_AREA_FOR_PROPERTIES = gql`
  query ListProperties($areaId: ID!) {
    listedPropertiesInSearchArea(areaId: $areaId) {
      id: externalId
    }
  }
`;

const sanitiseArea = (area) => {
  if (!area) {
    return null;
  }

  const [label, value] = area.split("-");
  if (!label || !value) {
    return null;
  }

  return {
    label,
    value,
  };
};

const sanitiseBedrooms = (bedroomsString) => {
  if (!bedroomsString) {
    return undefined;
  }

  const parsed = parseInt(bedroomsString, 10);
  if (![1, 2, 3, 4, 5, 6, 9].includes(parsed)) {
    return undefined;
  }
  return parsed;
};

const sanitiseSort = (sort) => {
  if (!sort) {
    return undefined;
  }

  if (!["asc", "desc"].includes(sort)) {
    return undefined;
  }

  return sort;
};

const sanitiseType = (type) => {
  if (!type) {
    return undefined;
  }
  if (!PROPERTY_TYPES.includes(type)) {
    return undefined;
  }
  return type;
};

const applySorting =
  ({ sortDirection = "desc" }) =>
  (properties) => {
    return orderBy(properties, ["price", "externalId"], [sortDirection, "asc"]);
  };

const applyBedroomsFilter =
  ({ minBeds = 0, maxBeds = 9 }) =>
  (properties) => {
    const min = minBeds === "ALL" ? 0 : minBeds;
    const max = maxBeds === "ALL" ? 9 : maxBeds;

    return properties.filter(({ bedrooms = 0 }) => {
      return bedrooms >= min && bedrooms <= max;
    });
  };

const applyPropertyTypeFilter =
  ({ propertyType: type }) =>
  (properties) => {
    if (!type || type === "ALL") {
      return properties;
    }
    return properties.filter(({ propertyType }) => propertyType === type);
  };

const applyAreaFilter =
  ({ area, deals }) =>
  (properties) => {
    if (!area) {
      return properties;
    }

    return properties.filter(({ externalId }) => deals.includes(externalId));
  };

/*
 * These are applied right to left, and it is important that sorting happens last
 * to prevent wasting time sorting filtered out deals
 */
const applyFilters = (filters) =>
  compose(
    applySorting(filters),
    applyAreaFilter(filters),
    applyBedroomsFilter(filters),
    applyPropertyTypeFilter(filters),
  );

export const usePropertyFilters = (properties) => {
  const location = useLocation();
  const history = useHistory();
  const params = qs.parse(location.search);

  const [area, setArea] = useState(sanitiseArea(params.area));
  const [minBeds, setMinBeds] = useState(sanitiseBedrooms(params.minBeds));
  const [maxBeds, setMaxBeds] = useState(sanitiseBedrooms(params.maxBeds));
  const [propertyType, setPropertyType] = useState(
    sanitiseType(params.propertyType),
  );
  const [sortDirection, setSortDirection] = useState(
    sanitiseSort(params.sortDirection),
  );

  const resetFilters = () => {
    setArea(null);
    setMinBeds(undefined);
    setMaxBeds(undefined);
    setPropertyType(undefined);
    setSortDirection(undefined);
  };

  const {
    previousData,
    data = previousData,
    loading,
  } = useQuery(SEARCH_AREA_FOR_PROPERTIES, {
    skip: !area,
    variables: {
      areaId: area?.value,
    },
  });

  useEffect(() => {
    const areaString = area ? `${area.label}-${area.value}` : undefined;
    const updatedSearch = qs.stringify({
      ...params,
      area: areaString,
      minBeds,
      maxBeds,
      propertyType,
      sortDirection,
    });

    history.replace({
      pathname: location.pathname,
      search: updatedSearch,
    });
  }, [area, minBeds, maxBeds, propertyType, sortDirection]);

  const dealsInSearchArea = data?.listedPropertiesInSearchArea || [];
  const deals = dealsInSearchArea.map(({ id }) => id);

  const filteredProperties = useMemo(
    () =>
      applyFilters({
        area,
        deals,
        minBeds,
        maxBeds,
        propertyType,
        sortDirection,
      })(properties),
    [area, deals, minBeds, maxBeds, propertyType, sortDirection, properties],
  );

  return {
    area,
    setArea,
    minBeds,
    setMinBeds,
    maxBeds,
    setMaxBeds,
    sortDirection,
    setSortDirection,
    propertyType,
    setPropertyType,
    filteredProperties,
    loading,
    resetFilters,
  };
};
